import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Input,
  DatePicker,
  Button,
  List,
  Space,
  message,
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import Constants from "../Utils/Constants";

const { RangePicker } = DatePicker;

export function ProgrammeAssign() {
  const { t } = useTranslation("programme", { useSuspense: false });
  const { patientId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  const authHeaders = {
    Authorization: "Bearer " + token,
  };
  const jsonHeaders = {
    ...authHeaders,
    "Content-Type": "application/json",
  };

  const patientName = location.state?.patientName || `Patient #${patientId}`;

  const [cyclesCatalog, setCyclesCatalog] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCycles, setSelectedCycles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingCycles, setLoadingCycles] = useState(true);

  // ------------------ Charger le catalogue de cycles ------------------
  useEffect(() => {
    const fetchCycles = async () => {
      try {
        setLoadingCycles(true);
        const res = await axios.get(`${Constants.SERVER_URL}/cycles`, {
          headers: authHeaders,
        });
        const data = Array.isArray(res.data) ? res.data : [];
        setCyclesCatalog(data);
      } catch (err) {
        console.error("Erreur GET /cycles :", err);
        message.error(
          t("label_error_loading_cycles") ||
            "Impossible de charger les cycles"
        );
        setCyclesCatalog([]);
      } finally {
        setLoadingCycles(false);
      }
    };

    fetchCycles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------ Filtre du catalogue ------------------
  const filteredCatalog = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cyclesCatalog;
    return cyclesCatalog.filter((c) =>
      (c.name || "").toLowerCase().includes(q)
    );
  }, [search, cyclesCatalog]);

  // ------------------ Gestion des cycles choisis ------------------
  const handleAddCycle = (cycle) => {
    setSelectedCycles((prev) => [
      ...prev,
      {
        weeklyCycleId: cycle.id,
        name: cycle.name,
        order: prev.length + 1,
        startDate: null,
        endDate: null,
      },
    ]);
  };

  const handleDatesChange = (index, dates) => {
    const [start, end] = dates || [];
    setSelectedCycles((prev) =>
      prev.map((c, i) =>
        i === index
          ? {
              ...c,
              startDate: start ? start.toISOString() : null,
              endDate: end ? end.toISOString() : null,
            }
          : c
      )
    );
  };

  const handleRemoveCycle = (index) => {
    setSelectedCycles((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((c, i) => ({ ...c, order: i + 1 }))
    );
  };

  // ------------------ D�sactiver les jours d�j� utilis�s ------------------
  /**
   * Retourne true si "date" appartient � la p�riode d�un autre cycle
   * (startDate/endDate inclus), pour griser ce jour dans le calendrier.
   */
  const isDateInExistingCycle = (currentIndex, date) => {
    if (!date) return false;
    const d = date.startOf("day");

    return selectedCycles.some((cycle, index) => {
      // on ignore le cycle qu�on est en train d��diter
      if (index === currentIndex) return false;
      if (!cycle.startDate || !cycle.endDate) return false;

      const start = dayjs(cycle.startDate).startOf("day");
      const end = dayjs(cycle.endDate).startOf("day");

      // d ? [start, end] ?
      return (
        d.isSame(start, "day") ||
        d.isSame(end, "day") ||
        (d.isAfter(start, "day") && d.isBefore(end, "day"))
      );
    });
  };

  // ------------------ Sauvegarde du programme ------------------
  const handleSaveProgramme = async () => {
    if (selectedCycles.length === 0) {
      message.warning(
        t("label_add_at_least_one_cycle") ||
          "Ajoute au moins un cycle au programme."
      );
      return;
    }

    const missingDates = selectedCycles.some(
      (c) => !c.startDate || !c.endDate
    );
    if (missingDates) {
      message.warning(
        t("label_each_cycle_needs_dates") ||
          "Chaque cycle doit avoir une date de d�but et de fin."
      );
      return;
    }

    // ?? V�rification serveur : pas de chevauchement entre les p�riodes
    const sorted = [...selectedCycles].sort((a, b) =>
      dayjs(a.startDate).diff(dayjs(b.startDate))
    );
    for (let i = 1; i < sorted.length; i++) {
      const prevEnd = dayjs(sorted[i - 1].endDate).startOf("day");
      const curStart = dayjs(sorted[i].startDate).startOf("day");
      if (!curStart.isAfter(prevEnd, "day")) {
        message.warning(
          "Les p�riodes des cycles ne doivent pas se chevaucher."
        );
        return;
      }
    }

    // On transforme en objets dayjs
    const startDates = selectedCycles.map((c) => dayjs(c.startDate));
    const endDates = selectedCycles.map((c) => dayjs(c.endDate));

    const globalStart = startDates.reduce(
      (min, d) => (d.isBefore(min) ? d : min),
      startDates[0]
    );

    const globalEnd = endDates.reduce(
      (max, d) => (d.isAfter(max) ? d : max),
      endDates[0]
    );

    const durationWeeks = Math.max(
      1,
      Math.ceil(globalEnd.diff(globalStart, "day") / 7)
    );

    const payload = {
      name: `Programme - ${patientName} - ${dayjs().format("YYYYMMDD-HHmmss")}`,
      PatientId: Number(patientId),
      startDate: globalStart.toISOString(),
      endDate: globalEnd.toISOString(),
      durationWeeks,
      timeOfDay: null,
      frequencyDays: [],
      status: "ACTIVE",
      cycles: selectedCycles.map((c, index) => ({
        WeeklyCycleId: c.weeklyCycleId,
        order: index + 1,
        startDate: c.startDate,
        endDate: c.endDate,
      })),
    };

    try {
      setSaving(true);
      const res = await axios.post(
        `${Constants.SERVER_URL}/programme/programmes`,
        payload,
        { headers: jsonHeaders }
      );
      message.success(
        t("label_programme_created") || "Programme cr�� avec succ�s"
      );
      const id = res?.data?.id;
      if (id) {
        navigate(`/programme/${id}`);
      }
    } catch (err) {
      console.error("Erreur POST /Programme/programmes :", err);
      message.error(
        err?.response?.data?.error ||
          err?.message ||
          t("label_error_saving_programme") ||
          "Erreur lors de la cr�ation du programme"
      );
    } finally {
      setSaving(false);
    }
  };

  // ------------------ Rendu ------------------
  return (
    <div style={{ padding: 24 }}>
      <h2>Programme pour : {patientName}</h2>
      <Row gutter={24}>
        {/* Colonne gauche : cycles s�lectionn�s */}
        <Col span={14}>
          <Card
            title={t("label_programme_cycles") || "Cycles du programme"}
            extra={
              <Button
                type="primary"
                onClick={handleSaveProgramme}
                loading={saving}
              >
                {t("label_save_programme") || "Enregistrer le programme"}
              </Button>
            }
          >
            {selectedCycles.length === 0 ? (
              <p>
                {t("label_add_cycle_from_catalog") ||
                  "Ajoute un cycle depuis le catalogue pour commencer."}
              </p>
            ) : (
              <List
                dataSource={selectedCycles}
                renderItem={(item, index) => (
                  <List.Item
                    actions={[
                      <Button
                        danger
                        size="small"
                        onClick={() => handleRemoveCycle(index)}
                        key="remove"
                      >
                        {t("label_delete") || "Supprimer"}
                      </Button>,
                    ]}
                  >
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <strong>
                        {item.order}. {item.name}
                      </strong>
                      <RangePicker
                        format="DD/MM/YYYY"
                        value={
                          item.startDate && item.endDate
                            ? [
                                dayjs(item.startDate),
                                dayjs(item.endDate),
                              ]
                            : null
                        }
                        onChange={(dates) => handleDatesChange(index, dates)}
                        // ?? griser les jours d�j� pris par d'autres cycles
                        disabledDate={(current) =>
                          isDateInExistingCycle(index, current)
                        }
                      />
                    </Space>
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>

        {/* Colonne droite : catalogue de cycles */}
        <Col span={10}>
          <Card
            title={t("label_cycle_catalog") || "Catalogue de cycles"}
            extra={
              <Input
                placeholder={
                  t("label_filter_cycle_name") || "Filtrer par nom de cycle"
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
              />
            }
          >
            {loadingCycles ? (
              <p>Chargement...</p>
            ) : (
              <List
                dataSource={filteredCatalog}
                locale={{
                  emptyText: t("label_no_cycle") || "No data",
                }}
                renderItem={(c) => (
                  <List.Item
                    actions={[
                      <Button
                        type="link"
                        onClick={() => handleAddCycle(c)}
                        key="add"
                      >
                        {t("label_add") || "Ajouter"}
                      </Button>,
                    ]}
                  >
                    <div>
                      <strong>{c.name}</strong>
                      {c.description && (
                        <div style={{ fontSize: 12, color: "#888" }}>
                          {c.description}
                        </div>
                      )}
                    </div>
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
